import { toRef, toRefs, watch, computed, reactive, ref, unref } from '@vue/composition-api';
import { useMutation, useQuery } from 'vue-query';
import useVuelidate from '@vuelidate/core';
import { BerlinBffContext } from '@services/BerlinBff';
import { useContext } from '@berlin-front/common/composables/context';
import useOrAll from '@berlin-front/common/composables/useOrAll';
import useFakeProgress from '@berlin-front/common/composables/useFakeProgress';
import { useRouter } from '@berlin-front/common/composables/useRouter';
import useErrors from '@composables/useErrors';
import { getErrors, maxLength, email, required } from '@berlin-front/common/composables/validator';
import { event } from 'vue-gtag';
import { ApiCacheContext } from '@/services/ApiCacheContext';
import { getRefDeep, getToDayJp, isValidDate, windowOpenNewTab } from '@berlin-front/common/utils';
import { ROUTE_NAME } from '@/router/route-name';
import { MemberRequestContext } from '@views/member/MemberRequestContext';
import { AuthLoginNpMemberContextType } from './context';

export default {
  useLogin: (): ReturnType<AuthLoginNpMemberContextType['useLogin']> => {
    const router = useRouter();
    const apiCacheContext = useContext(ApiCacheContext);
    const { fufururu, zipAddress } = useContext(BerlinBffContext);
    const { registerMemberFormState: state, normalize } = useContext(MemberRequestContext);
    const { errors, clearAll } = useErrors({
      fieldMapper: {},
      defaultFieldName: 'flashMessages',
    });

    const messages = computed(() => unref(errors)?.flashMessages ?? []);

    // npログイン（ワンタイムトークン発行）の呼び出し(submit)
    const {
      isSuccess: successFufururuTokenPost,
      isError: errorFufururuTokenPost,
      mutate,
    } = useMutation(
      'atone.fufururuTokenPost',
      () =>
        fufururu.atoneFufururuControllerFufururuTokenPost(getRefDeep(apiCacheContext.tokens, 'requestToken'), {
          npId: state.mailAddress,
          password: state.password,
        }),
      {
        retry: false,
        onSuccess: ({ data: fufururuTokenPostResult }) => {
          apiCacheContext.setAtoneFufururuTokenPostResult(fufururuTokenPostResult);
        },
      },
    );

    // ワンタイムトークン情報取得。ワンタイムトークン発行に成功したら、呼び出し。
    const { isSuccess: successFufururuInformGet, isError: errorFufururuInformGet } = useQuery(
      'atone.fufururuTokenInform',
      () =>
        fufururu.atoneFufururuControllerFufururuInform(
          getRefDeep(apiCacheContext.tokens, 'requestToken'),
          getRefDeep(apiCacheContext.atoneFufururuTokenPostResult, 'oneTimeToken'),
        ),
      {
        enabled: computed(() => unref(successFufururuTokenPost)),
        onSuccess: ({ data: atoneFufururuInformGetResult }) => {
          apiCacheContext.setAtoneFufururuInformGetResult(atoneFufururuInformGetResult);
          // 加盟店情報から取得した顧客情報、NP会員情報から必要な情報を入力フォームにプリセットします。
          const familyName = state.familyName || atoneFufururuInformGetResult?.familyName || '';
          const familyNameKana = state.familyNameKana || atoneFufururuInformGetResult?.familyNameKana || '';
          const givenName = state.givenName || atoneFufururuInformGetResult?.givenName || '';
          const givenNameKana = state.givenNameKana || atoneFufururuInformGetResult?.givenNameKana || '';
          const sexDivision = state.sexDivision || atoneFufururuInformGetResult?.sexDivision || 2;
          const initialBirthday = atoneFufururuInformGetResult?.birthday || '';
          // 決済パラメータで既にzip設定済み？
          const zipAlreadySet = state.zip.length > 0;
          // 設定済みでなければfufururu/informの住所情報をセットする。
          const zip = zipAlreadySet
            ? state.zip
            : (atoneFufururuInformGetResult?.zipF3 || '') + (atoneFufururuInformGetResult?.zipB4 || '');
          const address2Second = zipAlreadySet
            ? state.address2Second
            : (atoneFufururuInformGetResult?.address2 || '') + (atoneFufururuInformGetResult?.address3 || '');
          const phoneNumber = state.phoneNumber || atoneFufururuInformGetResult?.phoneNumber || '';

          // GA4 プリセットイベント送信
          if (
            state.familyName ||
            state.givenName ||
            state.familyNameKana ||
            state.givenNameKana ||
            state.sexDivision ||
            state.zip ||
            (state.zip && state.address2Second) ||
            state.phoneNumber
          ) {
            event('preset', {
              preset_any_member_data: 'yes',
            });
          } else {
            event('preset', {
              preset_any_member_data: 'no',
            });
          }

          const birthdayArray =
            isValidDate(initialBirthday) &&
            Date.parse(`${getToDayJp()}T00:00:00Z`) >= Date.parse(`${initialBirthday}T00:00:00Z`) &&
            Date.parse(`${initialBirthday}T00:00:00Z`) >= Date.parse('1900-01-01T00:00:00Z')
              ? initialBirthday.split('-')
              : ['', '', ''];
          const birthdayYear = state.birthdayYear || birthdayArray[0];
          const birthdayMonth =
            state.birthdayMonth || birthdayArray[1] ? birthdayArray[1].padStart(2, '0') : birthdayArray[1];
          const birthdayDay =
            state.birthdayDay || birthdayArray[2] ? birthdayArray[2].replace(/^0+/, '') : birthdayArray[2];

          Object.assign(state, {
            familyName,
            familyNameKana,
            givenName,
            givenNameKana,
            sexDivision,
            zip,
            address2Second,
            phoneNumber,
            birthdayYear,
            birthdayMonth,
            birthdayDay,
          });
        },
      },
    );

    // 住所情報検索。ワンタイムトークン情報取得に成功したら、呼び出し。
    const { isSuccess: successZipAddress, isError: errorZipAddress } = useQuery(
      'atone.zipAddress',
      () =>
        zipAddress.atoneZipAddressControllerZipAddressGet(
          getRefDeep(apiCacheContext.atoneFufururuInformGetResult, 'zipF3'),
          getRefDeep(apiCacheContext.atoneFufururuInformGetResult, 'zipB4'),
        ),
      {
        enabled: computed(() => unref(successFufururuInformGet)),
        onSuccess: ({ data: atoneZipAddressGet }) => {
          apiCacheContext.setAtoneZipAddressGetResult(atoneZipAddressGet);
        },
      },
    );

    const isLoadingApi = ref(false);
    const isError = useOrAll(errorFufururuTokenPost, errorFufururuInformGet);
    const isSuccess = useOrAll(
      computed(() => unref(successFufururuInformGet) && unref(errorZipAddress)), // 会員情報取得後、郵便番号に該当する住所検索結果なし
      successZipAddress, // 住所検索まで成功
    );

    // 成功、または失敗になったらisEnded
    const isEnded = useOrAll(isSuccess, isError);

    // 進捗。useMutationの状況に合わせてプログレスを進める。
    const {
      progress,
      completed: progressCompleted,
      isLoading: progressIsLoading,
    } = toRefs(useFakeProgress(5000, isLoadingApi, isEnded));

    const loginParameter = reactive({
      atoneId: undefined,
      mailAddress: toRef(state, 'mailAddress'),
      phoneNumber: undefined,
      password: toRef(state, 'password'),
    });

    // バリデーション
    const rules = computed(() => ({
      mailAddress: {
        required,
        maxLength: maxLength(256),
        email,
      },

      password: {
        required,
        maxLength: maxLength(30),
      },
    }));
    const v$ = useVuelidate(rules, loginParameter);
    const getError = getErrors(v$);

    const isSubmitButtonDisabled = computed(
      () =>
        state.mailAddress === '' ||
        state.password === '' ||
        unref(getError)('mailAddress').$error ||
        unref(getError)('password').$error,
    );

    // そのままmutateをキックするのではなく、
    // - validation結果を確認
    // - 実行中でないことを確認
    // - 実行後（正常系）でないことを確認
    // した上でリクエストを行う。
    const onSubmit = async () => {
      if ((await v$.value.$validate()) && !unref(isLoadingApi) && !unref(isSuccess)) {
        clearAll();
        isLoadingApi.value = true;
        mutate();
      }
    };

    const openMyPageRemind = () => {
      windowOpenNewTab(process.env.VUE_APP_NP_POINT_PASSWORD_REMIND_URL);
    };

    // 「戻る」ボタン押下時に前の画面へ遷移する処理
    const goBack = async () => {
      await router.replace({ name: ROUTE_NAME.ATONEAUTH_INTRO });
    };

    watch(progressCompleted, (result) => {
      if (result) {
        if (unref(isSuccess)) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          router.replace({ name: ROUTE_NAME.ATONEAUTH_NP_MEMBER_REGISTER });
          return;
        }
      }
      isLoadingApi.value = false;
    });

    // return
    return reactive({
      title: 'NP会員IDでatoneに連携',
      state: loginParameter,

      // progress
      progress,
      progressIsLoading,
      messages,

      submitButton: {
        isDisabled: isSubmitButtonDisabled,
        onClick: onSubmit,
      },

      // action
      openMyPageRemind,
      showHelp: undefined,
      goBack,
      normalize,

      // validator
      v$,
      getError,

      TERMS_OF_USE_URL: process.env.VUE_APP_ATONE_TERMS_OF_USE_URL,
    });
  },
} satisfies AuthLoginNpMemberContextType;
